import { z } from 'zod';
import { leadFiltersSchema } from './leads';
import { appointmentDetailsSchema } from './interactions';
import { Currency, FinancingType, InteractionType, PipelineStage } from '../enums';
import { ANNUL_DEAL_REASON_MIN } from './commercial';
import { nextStepSchema } from './interactions';
import type { LeadListItem } from './leads';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

const CONTACT_TYPES = [
  InteractionType.LLAMADA,
  InteractionType.WHATSAPP,
  InteractionType.MAIL,
  InteractionType.SMS,
  InteractionType.REUNION,
] as const;

/**
 * Mensaje del esquema cuando una separación llega sin unidades (Lote 5 · H2, P7). El esquema no conoce los tipos
 * contables de la empresa: la web y la API usan `separationNeedsUnitMessage(settings.countableUnitTypes)`.
 */
export const SEPARATION_NEEDS_UNIT_MESSAGE = 'Elige al menos una unidad';

/**
 * Mover un lead a una etapa del kanban = registrar el hecho que la justifica (ADR-007).
 * Cada etapa exige sus datos mínimos; las etapas se derivan de los hechos, nunca se "pintan".
 */
export const stageMoveSchema = z.discriminatedUnion('stage', [
  z.object({
    stage: z.literal(PipelineStage.CONTACTADO),
    contact: z.object({ type: z.enum(CONTACT_TYPES), description: optionalText(2000) }),
    next: nextStepSchema,
  }),
  z.object({
    stage: z.literal(PipelineStage.CITA),
    appointment: z.object({
      scheduledAt: z.coerce.date(),
      description: optionalText(500),
      details: appointmentDetailsSchema.optional().nullable(),
    }),
  }),
  z.object({
    stage: z.literal(PipelineStage.VISITA),
    visit: z.object({
      visitAt: z.coerce.date(),
      description: optionalText(2000),
      walkIn: z.boolean().default(false),
      unitId: z.string().uuid().optional().nullable(),
    }),
    next: nextStepSchema,
  }),
  z.object({
    stage: z.literal(PipelineStage.PROFORMA),
    quote: z.object({
      unitId: z.string().uuid().optional().nullable(),
      price: z.coerce.number().positive('Ingresa el precio'),
      /** Sin valor = la moneda de la unidad o, sin unidad, la de la empresa (un default en US$ chocaba con unidades en soles). */
      currency: z.nativeEnum(Currency).optional().nullable(),
      financingType: z.nativeEnum(FinancingType).optional().nullable(),
      expiresAt: z.coerce.date().optional().nullable(),
    }),
    next: nextStepSchema,
  }),
  z.object({
    stage: z.literal(PipelineStage.SEPARACION),
    deal: z
      .object({
        startedAt: z.coerce.date(),
        unitId: z.string().uuid().optional().nullable(),
        /** Varias unidades (p. ej. departamento + estacionamiento). Se combina con unitId. */
        unitIds: z.array(z.string().uuid()).max(10).optional(),
        /**
         * Proforma que se convierte (Lote 5 · H2, P8): debe ser del lead, estar VIGENTE o EXPIRADA y ser de una de las
         * unidades separadas. Sin valor, la API toma la más reciente de esas unidades; si no hay, no convierte ninguna.
         */
        quoteId: z.string().uuid().optional().nullable(),
        amountInitial: z.coerce.number().nonnegative().optional().nullable(),
        /** Moneda de la reserva (`amountInitial`); sin valor = la de reserva del proyecto (P9). */
        initialCurrency: z.nativeEnum(Currency).optional().nullable(),
        /** Moneda del precio: siempre la de la unidad principal; si viene otra, la API responde 400 (P9). */
        currency: z.nativeEnum(Currency).optional().nullable(),
        contractMode: z.nativeEnum(FinancingType).optional().nullable(),
        bank: optionalText(80),
        description: optionalText(1000),
      })
      // Al menos una unidad (P7): la contable (departamento, casa, lote) la exige la API, que conoce los tipos.
      .refine((d) => !!d.unitId || (d.unitIds?.length ?? 0) > 0, {
        message: SEPARATION_NEEDS_UNIT_MESSAGE,
        path: ['unitIds'],
      }),
    next: nextStepSchema,
  }),
  z.object({
    stage: z.literal(PipelineStage.VENTA),
    deal: z.object({
      finishedAt: z.coerce.date(),
      minuteSignedAt: z.coerce.date().optional().nullable(),
      unitId: z.string().uuid().optional().nullable(),
      /** Lo marcado se SUMA a las unidades separadas por el lead, que siempre entran en la venta (P6). */
      unitIds: z.array(z.string().uuid()).max(10).optional(),
      /** Proforma que se convierte cuando no hay separación con proforma (P8): mismas reglas que en la separación. */
      quoteId: z.string().uuid().optional().nullable(),
      amountTotal: z.coerce.number().positive().optional().nullable(),
      amountInitial: z.coerce.number().nonnegative().optional().nullable(),
      /** Moneda de la inicial; sin valor = la de la reserva de la separación (P9). */
      initialCurrency: z.nativeEnum(Currency).optional().nullable(),
      currency: z.nativeEnum(Currency).optional().nullable(),
      contractMode: z.nativeEnum(FinancingType).optional().nullable(),
      bank: optionalText(80),
      contractNumber: optionalText(60),
      /** Detalle (condiciones de la minuta…): queda como nota del lead, igual que en la separación (P13). */
      description: optionalText(1000),
    }),
  }),
  z.object({
    stage: z.literal(PipelineStage.PERDIDO),
    desist: z.object({
      reason: z.string().min(1, 'Selecciona un motivo').max(40),
      comment: optionalText(1000),
    }),
  }),
]);
export type StageMoveInput = z.infer<typeof stageMoveSchema>;
export type StageMovePayload = z.input<typeof stageMoveSchema>;

/**
 * Retroceder en el kanban = anular el hecho que sostiene la etapa actual (ADR-011). La etapa resultante se deriva.
 */
export const stageUndoSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('CANCEL_APPOINTMENT'),
    interactionId: z.string().uuid().optional().nullable(),
    reason: optionalText(500),
    next: nextStepSchema.optional(),
  }),
  z.object({
    action: z.literal('ANNUL_QUOTE'),
    /** Sin id = todas las proformas no anuladas del lead. */
    quoteId: z.string().uuid().optional().nullable(),
    reason: z.string().trim().min(2, 'Indica el motivo').max(300),
    next: nextStepSchema.optional(),
  }),
  z.object({
    action: z.literal('REFUND_DEAL'),
    dealId: z.string().uuid().optional().nullable(),
    refundedAt: z.coerce.date(),
    dropReason: z.string().min(1, 'Selecciona el motivo').max(40),
    notes: optionalText(1000),
    next: nextStepSchema.optional(),
  }),
  z.object({
    action: z.literal('ANNUL_DEAL'),
    dealId: z.string().uuid().optional().nullable(),
    /** Mismo mínimo útil que `annulDealSchema`: anular borra el proceso del historial (Lote 5 · H1, P16). */
    reason: z
      .string()
      .trim()
      .min(
        ANNUL_DEAL_REASON_MIN,
        `Explica el motivo (al menos ${ANNUL_DEAL_REASON_MIN} caracteres)`,
      )
      .max(300),
    dropReason: optionalText(40),
    next: nextStepSchema.optional(),
  }),
]);
export type StageUndoInput = z.infer<typeof stageUndoSchema>;
export type StageUndoPayload = z.input<typeof stageUndoSchema>;

/**
 * Filtros del tablero: los mismos de la tabla (una sola condición, `listWhere`), sin orden ni cursor;
 * la etapa la pone cada columna. Un filtro nuevo en `leadFiltersSchema` solo hay que sumarlo aquí.
 */
export const pipelineQuerySchema = leadFiltersSchema
  .pick({
    companyId: true,
    projectId: true,
    advisorId: true,
    unassigned: true,
    followupState: true,
    overdueBand: true,
    overdueDays: true,
    actions: true,
    contactState: true,
    status: true,
    mediumCategory: true,
    q: true,
    capturedFrom: true,
    capturedTo: true,
    tagIds: true,
    custom: true,
  })
  .extend({
    /** Tarjetas por columna en la primera carga. */
    perColumn: z.coerce.number().int().min(5).max(100).default(25),
  });
export type PipelineQuery = z.infer<typeof pipelineQuerySchema>;

export interface PipelineCard extends LeadListItem {
  daysInStage: number;
}

export interface PipelineColumn {
  stage: PipelineStage;
  count: number;
  items: PipelineCard[];
  nextCursor: string | null;
}

export interface PipelineBoard {
  columns: PipelineColumn[];
  total: number;
}

export interface StageMoveOptions {
  /**
   * `settings.requirePriorSeparationForSale` de la empresa (Lote 5 · H2, P12): con la regla activa, la API solo
   * acepta una venta con separación o proforma previa, así que «Venta» se ofrece únicamente desde PROFORMA o
   * SEPARACION (la etapa se deriva de esos hechos). Separación desde Nuevo o Contactado sigue permitida.
   */
  requirePriorSeparationForSale?: boolean;
}

/** Etapas a las que se puede mover desde la actual (solo hacia adelante + PERDIDO; desde PERDIDO se reabre). */
export function allowedStageMoves(
  current: PipelineStage,
  opts: StageMoveOptions = {},
): PipelineStage[] {
  const order: PipelineStage[] = [
    PipelineStage.NUEVO,
    PipelineStage.CONTACTADO,
    PipelineStage.CITA,
    PipelineStage.VISITA,
    PipelineStage.PROFORMA,
    PipelineStage.SEPARACION,
    PipelineStage.VENTA,
  ];
  if (current === PipelineStage.PERDIDO) return [];
  if (current === PipelineStage.VENTA) return [];
  const idx = order.indexOf(current);
  const next = [...order.slice(idx + 1), PipelineStage.PERDIDO];
  return opts.requirePriorSeparationForSale && !saleStage(current)
    ? next.filter((s) => s !== PipelineStage.VENTA)
    : next;
}

/** Etapas desde las que la API acepta la venta cuando exige separación o proforma previa (P12). */
export function saleStage(current: PipelineStage): boolean {
  return current === PipelineStage.PROFORMA || current === PipelineStage.SEPARACION;
}
